import React, { useEffect, useRef, useState } from 'react';

import './slide.scss';

interface InheritedProps {
  isFullWidth: boolean;
}
interface SlideProps {
  __INHERITED_PROPS__?: InheritedProps;
  children?: React.ReactNode;
  className?: string;
  shouldLazyLoad?: Boolean;
  style?: object;
}

const Slide = React.forwardRef<HTMLDivElement, SlideProps>(function Slide(
  props,
  activeSlideRef
) {
  const {
    __INHERITED_PROPS__,
    children,
    className,
    shouldLazyLoad = false,
    style
  } = props;

  const isActiveSlide = Boolean(activeSlideRef);
  const [isInView, setIsInView] = useState(isActiveSlide);
  const slideRef = useRef<HTMLDivElement>(null);
  const slideTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  function toggleInView() {
    const slideElem = slideRef.current;

    if (slideElem) {
      const slideRect = slideElem.getBoundingClientRect();
      const offset = slideRect.width * 0.5;

      if (slideRect.left - offset < window.innerWidth) {
        setIsInView(true);
      }
    }
  }

  if (shouldLazyLoad && !isInView && slideRef.current) {
    slideTimerRef.current = setTimeout(() => {
      toggleInView();
    }, 400);
  }

  useEffect(() => {
    if (!isInView && isActiveSlide) {
      setIsInView(true);
    }
  }, [isInView, isActiveSlide]);

  useEffect(() => {
    if (shouldLazyLoad) {
      toggleInView();
    }

    return () => {
      if (slideTimerRef.current) {
        clearTimeout(slideTimerRef.current as unknown as number);
      }
    };
  }, [shouldLazyLoad]);

  return (
    <div
      className={className}
      ref={activeSlideRef || slideRef}
      style={style}
      styleName={
        __INHERITED_PROPS__?.isFullWidth ? 'container-full-width' : 'container'
      }
    >
      {isInView || !shouldLazyLoad ? children : null}
    </div>
  );
});

Slide.displayName = 'Slide';

export default Slide;
